'use client';

import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { CheckCircle, AlertCircle, Info } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
    id: number;
    message: string;
    type: ToastType;
}

interface ToastContextValue {
    toast: (_message: string, _type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => { } });

export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const counter = useRef(0);
    // Every dismissal timeout the provider owns. Timers are tracked so the
    // unmount cleanup can cancel them: a dismissal callback that outlives
    // the provider fires into an unmounted React tree (and, in test workers,
    // into a disposed environment — "window is not defined").
    const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

    const toast = useCallback((message: string, type: ToastType = 'success') => {
        const id = ++counter.current;
        setToasts(prev => [...prev, { id, message, type }]);
        const timer = setTimeout(() => {
            timers.current.delete(timer);
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 4000);
        timers.current.add(timer);
    }, []);

    // Release every pending dismissal timer when the provider unmounts so no
    // callback (and no state update on the unmounted provider) can outlive it.
    useEffect(() => () => {
        timers.current.forEach(timer => clearTimeout(timer));
        timers.current.clear();
    }, []);

    // Stable context value — `toast` is already memoized, so the provider
    // value object should be too, otherwise every toast state change recreates
    // it and re-renders every consumer subtree.
    const contextValue = useMemo<ToastContextValue>(() => ({ toast }), [toast]);

    const icons = {
        success: <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />,
        error: <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />,
        info: <Info className="w-4 h-4 text-blue-400 flex-shrink-0" />,
    };

    const bgColors = {
        success: 'bg-emerald-500/10 border-emerald-500/20',
        error: 'bg-rose-500/10 border-rose-500/20',
        info: 'bg-blue-500/10 border-blue-500/20',
    };

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            {/* Toast container */}
            <div
                role="status"
                aria-live={toasts.some(t => t.type === 'error') ? 'assertive' : 'polite'}
                aria-atomic="true"
                className="fixed left-1/2 top-4 z-[100] flex w-[min(calc(100vw-2rem),24rem)] -translate-x-1/2 flex-col gap-2 pointer-events-none"
            >
                {toasts.map(t => (
                    <div
                        key={t.id}
                        className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl border shadow-2xl backdrop-blur-md animate-in slide-in-from-top-5 duration-200 ${bgColors[t.type]}`}
                    >
                        {icons[t.type]}
                        <span className="text-sm text-white flex-1">{t.message}</span>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}
